import { slugify } from './menuManager';

/**
 * Import de menu (Brique 3, phase B) — LOGIQUE PURE.
 * Transforme un texte de menu collé (typiquement copié depuis une page Uber Eats,
 * ou tapé à la main) en structure catégories → produits + prix.
 * 100 % pur → testable sans réseau. La pose en base est dans menuImportService.ts.
 *
 * Format reconnu (souple) :
 *   - Une CATÉGORIE = ligne titre : « ### Nom », « # Nom », ou ligne courte sans prix
 *     suivie de produits. Les marqueurs Markdown (#, *, -) sont tolérés.
 *   - Un PRODUIT = une ligne contenant un PRIX (« 7,00 € », « 10.50€ », « 12 € »).
 *     Le nom = ce qui précède le prix ; une éventuelle description suit le prix.
 *   - Les bruits Uber (« • 84% (86) », « Le nº 1 le plus aimé », « Servi avec… »)
 *     sont nettoyés du nom mais peuvent nourrir la description.
 */

export interface ImportedProduct {
  name: string;
  priceCents: number;
  description: string | null;
}
export interface ImportedCategory {
  name: string;
  emoji: string | null;
  products: ImportedProduct[];
}
export interface ImportedMenu {
  categories: ImportedCategory[];
  /** Compteurs pratiques pour l'aperçu. */
  totals: { categories: number; products: number };
}

/** Repère un prix dans une ligne et renvoie {cents, index} ou null. */
export function findPrice(line: string): { cents: number; start: number; end: number } | null {
  // ex : "7,00 €", "10.50€", "12 €", "1,45 €" — euro avant ou après l'espace.
  const re = /(\d{1,3}(?:[.,]\d{1,2})?)\s*(?:€|EUR|CHF)/i;
  const m = re.exec(line);
  if (!m) return null;
  const cents = Math.round(parseFloat(m[1].replace(',', '.')) * 100);
  return { cents, start: m.index, end: m.index + m[0].length };
}

/** Nettoie un nom de produit : retire marqueurs, notes Uber, espaces superflus. */
export function cleanName(raw: string): string {
  return raw
    .replace(/^[#>*\-\s]+/, '') // marqueurs Markdown/liste en tête
    .replace(/\s*•.*$/, '') // tout ce qui suit une puce « • 84% (86) »
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Nettoie une DESCRIPTION de produit (texte après le prix, ou champ pipe) :
 *  - retire une note de classement Uber en tête (« • 84% (86) », « 71% (7) ») ;
 *  - retire les puces résiduelles ;
 *  - retire un suffixe de classement Uber (« Le nº 1 le plus aimé »).
 * Garde les chiffres MÉTIER en tête (« 2 merguez… ») — contrairement à l'ancien
 * strip trop agressif qui les supprimait. Renvoie null si vide.
 */
export function cleanDescription(raw: string): string | null {
  let s = (raw ?? '').trim();
  s = s.replace(/^[•\s]*\d{1,3}\s*%\s*\(\d+\)\s*/, ''); // note Uber « • 84% (86) »
  s = s.replace(/^[•\s]+/, ''); // puces résiduelles
  s = s.replace(/\s*Le n[º°o]\s*\d+.*$/i, ''); // « Le nº 1 le plus aimé »
  s = s.replace(/\s+/g, ' ').trim();
  return s.length > 0 ? s : null;
}

/** Détecte un emoji éventuel en tête de ligne de catégorie (le garde à part). */
function extractEmoji(name: string): { emoji: string | null; rest: string } {
  const m = /^(\p{Extended_Pictographic})\s*(.*)$/u.exec(name);
  return m ? { emoji: m[1], rest: m[2].trim() } : { emoji: null, rest: name };
}

/**
 * Devine un emoji adapté au nom d'une catégorie (mots-clés FR/street-food).
 * Utilisé à l'import quand la catégorie n'a pas déjà un emoji explicite.
 * Insensible à la casse et aux accents. Repli : 🍴.
 */
export function guessCategoryEmoji(name: string): string {
  const n = name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, ''); // retire les accents
  // Ordre : du plus spécifique au plus générique.
  const rules: [RegExp, string][] = [
    [/taco/, '🌮'],
    [/burger|cheeseburger|hamburger/, '🍔'],
    [/panini/, '🥪'],
    [/sandwich|kebab|wrap|galette|tortilla/, '🥙'],
    [/pizza/, '🍕'],
    [/pates|pasta|spaghetti|lasagne/, '🍝'],
    [/frite|potato/, '🍟'],
    [/salade|crudite/, '🥗'],
    [/bowl|poke/, '🥗'],
    [/barquette|viande|grillade|brochette|kefta|merguez/, '🥩'],
    [/assiette|plat/, '🍽️'],
    [/wings|nugget|tender|tex.?mex|poulet|chicken/, '🍗'],
    [/naan|indien|tandoori/, '🫓'],
    [/dessert|tiramisu|glace|patisser|gateau|crepe|gaufre/, '🍰'],
    [/boisson|soda|coca|drink|jus|cafe|biere|vin/, '🥤'],
    [/enfant|kids/, '🧒'],
    [/entree|aperitif|snack|tapas|accompagnement/, '🍢'],
    [/soupe|veloute/, '🍲'],
    [/dejeuner|brunch|breakfast/, '🥐'],
  ];
  for (const [re, emoji] of rules) {
    if (re.test(n)) return emoji;
  }
  return '🍴';
}

/**
 * Une ligne est un TITRE DE CATÉGORIE si :
 *  - elle commence par « # » / « ### » (Markdown), OU
 *  - elle n'a pas de prix, est courte (≤ 6 mots), non vide, et n'est pas une
 *    ligne de description (« Servi avec… »).
 */
function looksLikeCategory(line: string): boolean {
  const t = line.trim();
  if (!t) return false;
  if (/^#{1,6}\s/.test(t)) return true;
  if (findPrice(t)) return false;
  const words = cleanName(t).split(' ').filter(Boolean);
  if (words.length === 0 || words.length > 6) return false;
  if (/^servi\b|^avec\b|^supplément|^au choix/i.test(cleanName(t))) return false;
  return true;
}

/**
 * Parse un texte de menu en structure exploitable.
 * Robuste aux lignes vides, aux marqueurs Markdown et au bruit Uber Eats.
 * Les produits rencontrés avant toute catégorie vont dans une catégorie « Menu ».
 */
export function parseMenuText(text: string): ImportedMenu {
  const categories: ImportedCategory[] = [];
  let current: ImportedCategory | null = null;

  const pushCategory = (name: string) => {
    const { emoji, rest } = extractEmoji(cleanName(name));
    const label = rest || 'Menu';
    // Emoji explicite gardé tel quel ; sinon deviné depuis le nom (jamais vide).
    current = { name: label, emoji: emoji ?? guessCategoryEmoji(label), products: [] };
    categories.push(current);
  };

  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;

    // Format PIPE explicite (émis par la vision IA) : « Nom | PRIX € | description ».
    // Non ambigu → prioritaire. Le nom est avant le prix, la description après.
    if (line.includes('|') && !/^#{1,6}\s/.test(line)) {
      const parts = line.split('|').map((s) => s.trim());
      const name = cleanName(parts[0] ?? '');
      const priceField = parts.find((p) => findPrice(p));
      const pr = priceField ? findPrice(priceField) : null;
      if (name && pr) {
        const priceIdx = parts.indexOf(priceField!);
        const description = cleanDescription(parts.slice(priceIdx + 1).join(' '));
        if (!current) pushCategory('Menu');
        current!.products.push({ name, priceCents: pr.cents, description });
        continue;
      }
      // pipe sans prix exploitable → on retombe sur le traitement standard ci-dessous
    }

    const price = findPrice(line);
    if (price) {
      // Ligne produit. Nom = avant le prix ; description = après (nettoyée).
      const name = cleanName(line.slice(0, price.start));
      if (!name) continue; // prix orphelin → ignoré
      const description = cleanDescription(line.slice(price.end));
      if (!current) pushCategory('Menu');
      current!.products.push({ name, priceCents: price.cents, description });
    } else if (looksLikeCategory(line)) {
      pushCategory(line);
    } else if (categories.length > 0 && categories[categories.length - 1].products.length > 0) {
      // Ligne ORPHELINE (pas de prix, pas un titre de catégorie) qui suit un produit :
      // c'est presque toujours sa DESCRIPTION sur une ligne à part (fréquent quand on
      // colle un menu multi-lignes). On la rattache au dernier produit au lieu de la jeter.
      // (On passe par `categories` — bien typé — plutôt que `current`, dont le narrowing
      // via closure fait échouer TypeScript.)
      const lastCat = categories[categories.length - 1];
      const last = lastCat.products[lastCat.products.length - 1];
      const extra = cleanDescription(line);
      if (extra) last.description = last.description ? `${last.description} ${extra}` : extra;
    }
    // sinon (aucun produit courant) : ligne libre ignorée — rattachée à rien de fiable.
  }

  // On ne garde que les catégories ayant au moins un produit.
  const kept = categories.filter((c) => c.products.length > 0);
  return {
    categories: kept,
    totals: {
      categories: kept.length,
      products: kept.reduce((n, c) => n + c.products.length, 0),
    },
  };
}

/** Slug unique de catégorie/produit à partir du nom (réutilise slugify du menu). */
export function importSlug(name: string): string {
  return slugify(name);
}
